import {
    Autocomplete,
    Avatar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid, IconButton,
    TextField, Tooltip, Typography
} from "@mui/material";
import NoData from "../no-data";
import {DeleteForever} from "@mui/icons-material";
import {useGenericQuery} from "../../services/react-query/generic.query";
import {UserTypes} from "../../types";
import {useState} from "react";
import {LoadingButton} from "@mui/lab";

const styles = {
    selectContainer: {
        marginTop: '16px',
        borderRadius: 1,
        border: '0.3px solid',
        overflow: 'auto',
        p: 1,
        maxHeight: '300px',
        minHeight: '120px'
    },
    selectItemName: {
        fontSize: '16px',
        color: '#919EAB',
        fontWeight: '400',
        fontFamily: 'Poppins, sans-serif',
        ml: 1.5,
    },
    selectItemContainer: {
        p: 1,
        ':hover': {
            background: '#F5F6F8',
            cursor: 'pointer',
        },
        height: '60px'
    }
}

export interface Props {
    visible: boolean,
    onClose: () => void,
    selectedUsersToNotify : UserTypes.Model[]
    setSelectedUsersToNotify:(users:UserTypes.Model[]) => void;
}

export const NotifyUsersModal: React.FC<Props> = ({ onClose, visible,selectedUsersToNotify, setSelectedUsersToNotify}) => {
    const {
        data: users,
        isLoading: isUsersLoading,
    } = useGenericQuery<UserTypes.Model[]>(
        'fetchUsersToNotify',
        'users/getSubscriptionUsers',
        undefined,
        undefined,
        false,
    );
    const [inputVal, setInputVal] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<UserTypes.Model[]>(selectedUsersToNotify?selectedUsersToNotify:[]);
    const [usersErrored, setUsersErrored] = useState<boolean>(false);

    const userIsSelected = (user: UserTypes.Model) => {
        return selectedUsers.filter(u => u.id === user.id || (u.firstName === user.firstName && u.lastName === user.lastName)).length > 0;
    }

    const selectUser = (user: UserTypes.Model) => {
        if (userIsSelected(user!)) {
            setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
        const input = document.getElementById('autocomplete-input-notify-user');
        if (input) {
            input.blur();
        }
    }

    const filterOptions = (options : UserTypes.Model[],inputValue : string) => {
        if(!inputValue) return [];
        return options.filter((user) => {
            return user.firstName?.toLowerCase().includes(inputValue.toLowerCase()) ||
                user.lastName?.toLowerCase().includes(inputValue.toLowerCase())
        })
    }
    return (
        <Dialog
            open={visible}
            onClose={onClose}
            maxWidth="lg"
        >
            <DialogTitle
                color={"#212B36"}
                fontWeight={500}
                fontFamily={'Poppins'}
                fontSize={'16px'}
            >
                Select Users to Notify this report
            </DialogTitle>
            <DialogContent sx={{maxWidth: {md:'900px'},minWidth: {md:'900px'},overflowX:'hidden'}}>
                <Grid container alignContent={'center'} justifyItems={'center'} alignItems={'center'}>
                    <Grid item md={6} xs={12}>
                        <Autocomplete
                            id="autocomplete-input-notify-user"
                            disablePortal
                            filterOptions={(options : UserTypes.Model[], {inputValue}) => {
                                return filterOptions(options, inputValue);
                            }}
                            sx={{ width: '100%' }}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    sx={{ marginTop: '16px' }}
                                    fullWidth
                                    label="Search Users"
                                />
                            }
                            getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}` || option?.firstName || option?.lastName || option?.backupEmail || option?.phoneNumber || ''}
                            onChange={(e, value) => {
                                return undefined
                            }}
                            clearOnBlur
                            onInputChange={(e, value) => {
                                setInputVal(value);
                            }}
                            noOptionsText={inputVal.length > 0 ? 'No users found' : 'Start typing to search users'}
                            options={users || []}
                            renderOption={(props, option) => !userIsSelected(option) && (
                                <Grid
                                    container
                                    key={option?.id}
                                    md={12}
                                    alignContent={'center'}
                                    alignItems={'center'}
                                    sx={{
                                        ...styles.selectItemContainer,
                                        borderRadius: 1,
                                        background: 'white'
                                    }}
                                    onClick={() => {
                                        selectUser(option);
                                    }}
                                >
                                    <Avatar sizes="small" key={option?.id} />
                                    <Typography sx={styles.selectItemName}>
                                        {option?.firstName} {option?.lastName}
                                    </Typography>
                                </Grid>
                            )}
                        />
                    </Grid>
                </Grid>

                <Grid
                    container
                    mb={3}
                    alignContent={'center'}
                    alignItems={'center'}
                    sx={{
                        ...styles.selectContainer,
                        borderColor: usersErrored ? 'red' : '#ccc'
                    }}
                >
                    {!isUsersLoading && selectedUsers?.length === 0 && (
                        <Grid container justifyContent={'center'} alignItems={'center'}>
                            <NoData
                                title="No selected users"
                                description="Select users to notify the report"
                                buttonText={null}
                                onClick={() => {}}
                            />
                        </Grid>
                    )}
                    {!isUsersLoading && selectedUsers?.map((user) => (
                        <Grid
                            container
                            key={user?.id}
                            md={4}
                            flexDirection={'row'}
                            alignContent={'center'}
                            alignItems={'center'}
                            sx={{
                                ...styles.selectItemContainer,
                                borderRadius: 1,
                                background: 'white',
                                ':hover': {}
                            }}
                        >
                            <Avatar sizes="small" key={user?.id} />
                            <Typography sx={styles.selectItemName}>
                                {user?.firstName} {user?.lastName}
                            </Typography>
                            <Tooltip title="Remove user">
                                <IconButton
                                    sx={{ ml: 1 }}
                                    onClick={() => {
                                        setSelectedUsers(selectedUsers.filter((t) => t.id !== user.id));
                                    }}
                                >
                                    <DeleteForever color="action" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    ))}
                </Grid>
                <DialogActions sx={{ mb: -2, mt: -2, mr: -3 }}>
                    <Grid
                        marginTop={20}
                        flexDirection={'row-reverse'}
                        container
                        spacing={2}
                        mt={3}
                    >
                        <LoadingButton
                            variant="alternate"
                            sx={{ ml: 1 }}
                            onClick={()=>{setSelectedUsersToNotify(selectedUsers);onClose();}}
                            disabled={selectedUsers.length===0 && selectedUsersToNotify.length===0}
                            loading={false}
                        >
                            {selectedUsersToNotify.length>0?'Update':'Add'}
                        </LoadingButton>
                        <LoadingButton
                            variant="outlined-alternate"
                            color="inherit"
                            onClick={onClose}
                            disabled={false}
                            loading={false}
                        >
                            Close
                        </LoadingButton>
                    </Grid>
                </DialogActions>

            </DialogContent>
        </Dialog>
    )
}